import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ContactForm from "@/components/feature/ContactForm";
import usePageMeta from "@/hooks/usePageMeta";
import { siteInfo } from "@/mocks/siteData";

const channels = [
  {
    icon: "ri-mail-send-line",
    title: "邮件联系",
    value: siteInfo.email,
    desc: "适合较长的说明、附件与引用授权申请，通常两个工作日内回复。",
  },
  {
    icon: "ri-lightbulb-line",
    title: "提供线索",
    value: "写明事件名称、时间与依据",
    desc: "线索越具体，核对越快。附上公开链接会大大提高我们采纳的概率。",
  },
  {
    icon: "ri-error-warning-line",
    title: "内容纠错",
    value: "请注明有出入的段落",
    desc: "确认后我们会更新正文，并在页面标注本次修订说明。",
  },
];

const processList = [
  { step: "01", title: "收到留言", desc: "编辑会在一个工作日内查看并分类。" },
  { step: "02", title: "核对信息", desc: "对照公开回放、平台说明与当事方回应。" },
  { step: "03", title: "回复结果", desc: "无论是否采纳，都会说明处理结果与原因。" },
];

export default function Contact() {
  const crumbs = [{ label: "首页", to: "/" }, { label: "联系我们" }];

  usePageMeta({
    title: "联系我们_提供线索与内容纠错 - 直播翻车观察局",
    description:
      "通过邮件或在线留言联系直播翻车观察局，提供网红翻车事件线索、指出内容错误或申请引用授权，编辑会在两个工作日内回复。",
    keywords: "联系我们,网红翻车线索,内容纠错,翻车事件投稿,引用授权",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ContactPage",
          name: "联系直播翻车观察局",
          description: "提供事件线索、内容纠错与引用授权申请的联系方式。",
          inLanguage: "zh-CN",
        },
        {
          "@type": "Organization",
          name: siteInfo.name,
          email: siteInfo.email,
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "内容反馈",
            email: siteInfo.email,
            availableLanguage: ["zh-CN"],
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: crumbs.map((crumb, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: crumb.label,
            item: crumb.to ? `https://fanche-observer.example${crumb.to}` : undefined,
          })),
        },
      ],
    },
  });

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-8 pb-10 md:pb-12 bg-background-100 border-b border-background-200">
        <Breadcrumb items={crumbs} />
        <div className="mt-6 max-w-3xl">
          <p className="flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-primary-600 mb-4 uppercase">
            <span className="w-6 h-px bg-primary-400"></span>
            Contact
          </p>
          <h1 className="font-heading font-black text-3xl md:text-5xl leading-tight text-foreground-950">
            联系我们
          </h1>
          <p className="mt-5 text-sm md:text-lg leading-relaxed text-foreground-600">
            提供事件线索、指出内容错误，或者申请引用站内整理，都可以从这里开始。
            我们不会把你的联系方式用于其他用途。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          <div className="lg:col-span-2 space-y-4">
            {channels.map((item) => (
              <div key={item.title} className="p-5 rounded-lg bg-background-100 border border-background-200">
                <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <h2 className="mt-3.5 font-heading font-semibold text-base text-foreground-950">{item.title}</h2>
                <p className="mt-1.5 text-sm text-primary-600 break-all">{item.value}</p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}

            <div className="p-5 rounded-lg bg-secondary-100 border border-secondary-200">
              <h2 className="font-heading font-semibold text-sm text-secondary-950">留言之后会发生什么</h2>
              <ol className="mt-4 space-y-4">
                {processList.map((item) => (
                  <li key={item.step} className="flex gap-3">
                    <span className="shrink-0 w-7 h-7 rounded-md bg-secondary-900 text-secondary-50 text-xs font-semibold flex items-center justify-center">
                      {item.step}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-secondary-950">{item.title}</span>
                      <span className="block mt-0.5 text-xs leading-relaxed text-secondary-900/85">{item.desc}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="lg:col-span-3">
            <ContactForm />

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link
                to="/faq"
                className="flex items-start gap-3 p-5 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors"
              >
                <span className="w-10 h-10 shrink-0 rounded-md bg-foreground-950 text-background-50 flex items-center justify-center">
                  <i className="ri-question-line text-lg"></i>
                </span>
                <span>
                  <span className="block font-heading font-semibold text-sm text-foreground-950">先看常见问题</span>
                  <span className="block mt-1 text-xs text-foreground-600 leading-relaxed">
                    收录范围、图片说明与指数计算方式都写在这里。
                  </span>
                </span>
              </Link>
              <Link
                to="/about"
                className="flex items-start gap-3 p-5 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors"
              >
                <span className="w-10 h-10 shrink-0 rounded-md bg-foreground-950 text-background-50 flex items-center justify-center">
                  <i className="ri-information-line text-lg"></i>
                </span>
                <span>
                  <span className="block font-heading font-semibold text-sm text-foreground-950">了解内容原则</span>
                  <span className="block mt-1 text-xs text-foreground-600 leading-relaxed">
                    哪些事件我们会做，哪些不做，都有明确说明。
                  </span>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}